import { Link, usePage, router } from "@inertiajs/react"
import { useState, useCallback, useEffect, useRef, type ReactNode } from "react"
import { Toaster } from "@/components/ui/sonner"
import FlashMessages from "@/components/flash-messages"
import ImpersonationBanner from "@/components/impersonation-banner"
import type { SharedProps } from "@/types"
import { cn } from "@/lib/utils"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { AvatarCreditRing } from "@/components/dashboard/credit-indicator"
import { useCreditBalance } from "@/hooks/use-credit-balance"


type NavItem = { href: string; label: string; icon: (props: { className?: string }) => React.JSX.Element }
type NavSection = { heading: string; items: NavItem[] }

const NAV_SECTIONS: NavSection[] = [
  {
    heading: "",
    items: [
      { href: "/dashboard", label: "Dashboard", icon: HomeIcon },
    ],
  },
  {
    heading: "Content",
    items: [
      { href: "/dashboard/listings", label: "Listings", icon: ListIcon },
      { href: "/dashboard/videos", label: "Videos", icon: VideoIcon },
      { href: "/dashboard/posts", label: "Posts", icon: PostIcon },
      { href: "/dashboard/calendar", label: "Calendar", icon: CalendarIcon },
    ],
  },
  {
    heading: "Insights",
    items: [
      { href: "/dashboard/analytics", label: "Analytics", icon: ChartIcon },
    ],
  },
  {
    heading: "Account",
    items: [
      { href: "/dashboard/connections", label: "Connections", icon: ShareIcon },
      { href: "/dashboard/members", label: "Team", icon: UsersIcon },
      { href: "/dashboard/settings", label: "Settings", icon: SettingsIcon },
    ],
  },
]

const COLLAPSED_KEY = "sidebar-collapsed"

export default function DashboardLayout({ children }: { children: ReactNode }) {
  const { currentUser, impersonating } = usePage<SharedProps>().props
  const { url } = usePage()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === "undefined") return false
    return localStorage.getItem(COLLAPSED_KEY) === "true"
  })
  const [hovered, setHovered] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const closeMobile = useCallback(() => setMobileOpen(false), [])

  useEffect(() => {
    return router.on("navigate", closeMobile)
  }, [closeMobile])

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev
      localStorage.setItem(COLLAPSED_KEY, String(next))
      if (next) setHovered(false)
      return next
    })
  }, [])

  const handleMouseEnter = useCallback(() => {
    if (!collapsed) return
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current)
    hoverTimeoutRef.current = setTimeout(() => setHovered(true), 80)
  }, [collapsed])

  const handleMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current)
    if (!menuOpen) setHovered(false)
  }, [menuOpen])

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current)
    }
  }, [])

  // Whether to show full sidebar content (labels, user menu, etc.)
  const expanded = !collapsed || hovered

  const initials = currentUser?.name
    ?.split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "?"

  return (
    <TooltipProvider delayDuration={0}>
      <div className={`flex min-h-screen bg-cream ${impersonating ? "pt-10" : ""}`}>
        <ImpersonationBanner />

        {/* Desktop sidebar spacer — reserves layout space */}
        <div
          className={cn(
            "hidden lg:block shrink-0 transition-[width] duration-300 ease-in-out",
            collapsed ? "w-[72px]" : "w-64",
          )}
        />

        {/* Desktop sidebar */}
        <aside
          className={cn(
            "hidden lg:flex fixed z-30 flex-col border-r border-stone/60 bg-white transition-all duration-300 ease-in-out",
            impersonating ? "top-10 h-[calc(100vh-2.5rem)]" : "top-0 h-screen",
            expanded ? "w-64" : "w-[72px]",
            collapsed && hovered && "shadow-xl shadow-charcoal/8",
          )}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className="flex h-full flex-col overflow-hidden">
            {/* Logo + toggle */}
            <div className={cn(
              "flex h-16 items-center border-b border-stone/40 transition-[padding] duration-300",
              expanded ? "px-5" : "px-3",
            )}>
              {expanded ? (
                <Link href="/" className="flex items-baseline text-xl font-display font-semibold tracking-tight whitespace-nowrap">
                  <span className="text-charcoal">Quite</span>
                  <span className="text-red">Right.</span>
                </Link>
              ) : (
                <Link href="/" className="flex items-baseline text-lg font-display font-semibold tracking-tight mx-auto">
                  <span className="text-charcoal">Q</span>
                  <span className="text-red">.</span>
                </Link>
              )}
              <button
                onClick={toggleCollapsed}
                className={cn(
                  "flex size-7 items-center justify-center rounded-md text-warm-grey hover:bg-stone/30 hover:text-charcoal transition-all duration-200",
                  expanded ? "ml-auto opacity-100" : "absolute -right-3 top-5 size-6 rounded-full bg-white border border-stone/60 shadow-sm opacity-0 group-hover/sidebar:opacity-100 hover:bg-stone/20",
                )}
                aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                <ToggleIcon className="size-3.5" collapsed={collapsed} />
              </button>
            </div>

            {/* Nav links */}
            <nav className={cn(
              "flex-1 overflow-y-auto overflow-x-hidden py-4 transition-[padding] duration-300",
              expanded ? "px-3" : "px-2",
            )}>
              <div className="space-y-5">
                {NAV_SECTIONS.map((section) => (
                  <div key={section.heading || "top"}>
                    {section.heading && expanded && (
                      <p className="mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-widest text-warm-grey/60">
                        {section.heading}
                      </p>
                    )}
                    {section.heading && !expanded && (
                      <div className="mx-auto mb-1.5 h-px w-6 bg-stone/40" />
                    )}
                    <div className="space-y-0.5">
                      {section.items.map((item) => {
                        const active = url === item.href || (item.href !== "/dashboard" && url.startsWith(item.href))
                        const Icon = item.icon
                        const link = (
                          <Link
                            key={item.href}
                            href={item.href}
                            className={cn(
                              "flex items-center rounded-lg text-sm font-medium transition-all duration-200",
                              expanded ? "gap-3 px-3 py-2" : "justify-center px-2 py-2",
                              active
                                ? "bg-stone/40 text-charcoal"
                                : "text-warm-grey hover:bg-stone/20 hover:text-charcoal",
                            )}
                          >
                            <Icon className="size-4.5 shrink-0" />
                            {expanded && (
                              <span className="whitespace-nowrap animate-in fade-in duration-200">
                                {item.label}
                              </span>
                            )}
                          </Link>
                        )

                        if (!expanded) {
                          return (
                            <Tooltip key={item.href}>
                              <TooltipTrigger asChild>{link}</TooltipTrigger>
                              <TooltipContent side="right" sideOffset={8}>
                                {item.label}
                              </TooltipContent>
                            </Tooltip>
                          )
                        }

                        return link
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </nav>

            {/* Credit warning */}
            <CreditWarning expanded={expanded} />

            {/* User section */}
            <div className="border-t border-stone/40 p-3">
              <UserMenu
                name={currentUser?.name || ""}
                email={currentUser?.email || ""}
                avatarUrl={currentUser?.avatar_url}
                initials={initials}
                isSuperadmin={currentUser?.is_superadmin || false}
                collapsed={!expanded}
                onMenuOpenChange={(open) => {
                  setMenuOpen(open)
                  if (!open) setHovered(false)
                }}
              />
            </div>
          </div>
        </aside>

        {/* Mobile top bar */}
        <div className="flex min-w-0 flex-1 flex-col">
          <header className="flex h-16 items-center justify-between border-b border-stone/60 bg-white px-4 lg:hidden">
            <Link href="/" className="flex items-baseline text-xl font-display font-semibold tracking-tight">
              <span className="text-charcoal">Quite</span>
              <span className="text-red">Right.</span>
            </Link>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="flex size-10 items-center justify-center rounded-full text-charcoal hover:bg-stone/40"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
            >
              {mobileOpen ? (
                <svg className="size-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="size-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                </svg>
              )}
            </button>
          </header>

          {/* Mobile nav overlay */}
          {mobileOpen && (
            <div className="border-b border-stone/60 bg-white p-4 lg:hidden">
              <nav className="space-y-4">
                {NAV_SECTIONS.map((section) => (
                  <div key={section.heading || "top"}>
                    {section.heading && (
                      <p className="mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-widest text-warm-grey/60">
                        {section.heading}
                      </p>
                    )}
                    <div className="space-y-0.5">
                      {section.items.map((item) => {
                        const active = url === item.href || (item.href !== "/dashboard" && url.startsWith(item.href))
                        const Icon = item.icon
                        return (
                          <Link
                            key={item.href}
                            href={item.href}
                            onClick={closeMobile}
                            className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                              active ? "bg-stone/40 text-charcoal" : "text-warm-grey hover:bg-stone/20"
                            }`}
                          >
                            <Icon className="size-5" />
                            {item.label}
                          </Link>
                        )
                      })}
                    </div>
                  </div>
                ))}
              </nav>
              <CreditWarning expanded />
              <div className="mt-4 border-t border-stone/40 pt-4">
                <MobileUserSection
                  avatarUrl={currentUser?.avatar_url}
                  initials={initials}
                  name={currentUser?.name || ""}
                  email={currentUser?.email || ""}
                  isSuperadmin={currentUser?.is_superadmin || false}
                  closeMobile={closeMobile}
                />
              </div>
            </div>
          )}

          {/* Main content */}
          <main className="flex-1 px-4 py-8 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-5xl">{children}</div>
          </main>
        </div>

        <FlashMessages />
        <Toaster position="top-right" richColors />
      </div>
    </TooltipProvider>
  )
}

function UserMenu({
  name,
  email,
  avatarUrl,
  initials,
  isSuperadmin,
  collapsed = false,
  onMenuOpenChange,
}: {
  name: string
  email: string
  avatarUrl?: string | null
  initials: string
  isSuperadmin: boolean
  collapsed?: boolean
  onMenuOpenChange?: (open: boolean) => void
}) {
  const { displayBalance } = useCreditBalance()

  const avatarWithRing = (
    <AvatarCreditRing size={collapsed ? 32 : 32}>
      <Avatar className="size-8">
        <AvatarImage src={avatarUrl || undefined} />
        <AvatarFallback className="bg-stone/40 text-xs">{initials}</AvatarFallback>
      </Avatar>
    </AvatarCreditRing>
  )

  return (
    <DropdownMenu onOpenChange={onMenuOpenChange}>
      {collapsed ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger className="flex w-full items-center justify-center rounded-lg px-2 py-2.5 text-left hover:bg-stone/20">
              {avatarWithRing}
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={8}>
            {displayBalance.toLocaleString()} credits
          </TooltipContent>
        </Tooltip>
      ) : (
        <DropdownMenuTrigger className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-stone/20">
          {avatarWithRing}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-charcoal">{name}</p>
            <p className="truncate text-xs text-warm-grey">{email}</p>
          </div>
        </DropdownMenuTrigger>
      )}
      <DropdownMenuContent align="start" className="w-56">
        <div className="px-2 py-1.5 text-xs text-warm-grey">
          {displayBalance.toLocaleString()} credits remaining
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link href="/dashboard/credits" className="flex items-center gap-2">
            <CreditsIcon className="size-4" />
            Credits
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link href="/dashboard/profile" className="flex items-center gap-2">
            <UserIcon className="size-4" />
            Profile
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link href="/dashboard/settings" className="flex items-center gap-2">
            <SettingsIcon className="size-4" />
            Account settings
          </Link>
        </DropdownMenuItem>
        {isSuperadmin && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link href="/admin" className="flex items-center gap-2">
                <ShieldIcon className="size-4" />
                Admin
              </Link>
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link href="/sign-out" method="delete" as="button" className="flex w-full items-center gap-2 text-left">
            <LogOutIcon className="size-4" />
            Sign out
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function MobileUserSection({
  avatarUrl,
  initials,
  name,
  email,
  isSuperadmin,
  closeMobile,
}: {
  avatarUrl?: string | null
  initials: string
  name: string
  email: string
  isSuperadmin: boolean
  closeMobile: () => void
}) {
  const { displayBalance } = useCreditBalance()

  return (
    <>
      <div className="flex items-center gap-3 px-3">
        <AvatarCreditRing size={32}>
          <Avatar className="size-8">
            <AvatarImage src={avatarUrl || undefined} />
            <AvatarFallback className="bg-stone/40 text-xs">{initials}</AvatarFallback>
          </Avatar>
        </AvatarCreditRing>
        <div className="text-sm">
          <p className="font-medium text-charcoal">{name}</p>
          <p className="text-xs text-warm-grey">{email}</p>
        </div>
      </div>
      <div className="mt-2 px-3">
        <p className="text-xs text-warm-grey">{displayBalance.toLocaleString()} credits remaining</p>
      </div>
      <Link
        href="/dashboard/credits"
        className="mt-3 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-warm-grey hover:bg-stone/20 hover:text-charcoal"
      >
        <CreditsIcon className="size-4" />
        Credits
      </Link>
      <Link
        href="/dashboard/profile"
        className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-warm-grey hover:bg-stone/20 hover:text-charcoal"
      >
        <UserIcon className="size-4" />
        Profile
      </Link>
      {isSuperadmin && (
        <Link
          href="/admin"
          onClick={closeMobile}
          className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-warm-grey hover:bg-stone/20 hover:text-charcoal"
        >
          <ShieldIcon className="size-4" />
          Admin
        </Link>
      )}
      <Link
        href="/sign-out"
        method="delete"
        as="button"
        className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-warm-grey hover:bg-stone/20 hover:text-charcoal"
      >
        <LogOutIcon className="size-4" />
        Sign out
      </Link>
    </>
  )
}

function CreditWarning({ expanded }: { expanded: boolean }) {
  const { balance } = useCreditBalance()
  if (balance > 0) return null

  return (
    <div className="px-3 pb-2">
      <Link
        href="/dashboard/credits"
        className={cn(
          "flex items-center gap-2 rounded-lg bg-red/10 text-red transition-colors hover:bg-red/15",
          expanded ? "px-3 py-2" : "justify-center px-2 py-2",
        )}
      >
        <svg className="size-4 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
        </svg>
        {expanded && (
          <span className="text-xs font-medium animate-in fade-in duration-200">
            No credits remaining
          </span>
        )}
      </Link>
    </div>
  )
}

function ToggleIcon({ className, collapsed }: { className?: string; collapsed: boolean }) {
  return (
    <svg
      className={cn(className, "transition-transform duration-300", collapsed && "rotate-180")}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
    </svg>
  )
}

function CreditsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0 1 15.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 0 1 3 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 0 0-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 0 1-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 0 0 3 15h-.75M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm3 0h.008v.008H18V10.5Zm-12 0h.008v.008H6V10.5Z" />
    </svg>
  )
}

function HomeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
    </svg>
  )
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
    </svg>
  )
}

function SettingsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
    </svg>
  )
}

function ListIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0ZM3.75 12h.007v.008H3.75V12Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm-.375 5.25h.007v.008H3.75v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
    </svg>
  )
}

function VideoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
    </svg>
  )
}

function UserIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
    </svg>
  )
}

function LogOutIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 9V5.25A2.25 2.25 0 0 1 10.5 3h6a2.25 2.25 0 0 1 2.25 2.25v13.5A2.25 2.25 0 0 1 16.5 21h-6a2.25 2.25 0 0 1-2.25-2.25V15m-3 0-3-3m0 0 3-3m-3 3H15" />
    </svg>
  )
}

function ShareIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185Z" />
    </svg>
  )
}

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
    </svg>
  )
}

function PostIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5" />
    </svg>
  )
}

function ChartIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
    </svg>
  )
}

function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z" />
    </svg>
  )
}
